import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Switch,
  TablePagination,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { ImpersonationReadOnlyBanner } from '../components/ImpersonationReadOnlyBanner';
import { ApiClient } from '../services/api';
import type { PartnerUserItem } from '../services/api';

/**
 * Roles that client admins can assign.
 * Excludes sophia_admin and partner (internal-only roles).
 * Array order is the display order mandated by SOPHIA-34850.
 */
const CLIENT_ROLES = [
  { key: 'client_admin', label: 'Partner Institution Admin' },
  { key: 'transcript_viewer', label: 'Transcript Viewer' },
  { key: 'academic_advisor', label: 'Student Summary Viewer' },
  { key: 'api_developer', label: 'API Developer' },
] as const;

/** Map role keys to human-readable labels */
const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  CLIENT_ROLES.map((r) => [r.key, r.label])
);

/** Roles hidden from display/assignment on this page */
const HIDDEN_ROLES = new Set(['sophia_admin', 'partner']);

function RoleChips({ roles }: { roles: string[] }) {
  const visible = roles.filter((r) => !HIDDEN_ROLES.has(r));
  if (visible.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No roles
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {visible.map((role) => (
        <Chip
          key={role}
          label={ROLE_LABELS[role] || role.replace(/_/g, ' ')}
          size="small"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
      ))}
    </Stack>
  );
}

type ModalMode = 'create' | 'edit' | null;

export function ClientUserManagement(): React.ReactElement {
  const { idToken, user: currentUser } = useAuth();
  // SOPHIA-35121: sophia_admin's list scopes to the switcher-selected partner
  const { activePartnerId, isInternal } = usePartnerScope();

  const [users, setUsers] = useState<PartnerUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editUid, setEditUid] = useState<string | null>(null);
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRoles, setFormRoles] = useState<string[]>([]);
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Remove confirmation dialog
  const [removeUser, setRemoveUser] = useState<PartnerUserItem | null>(null);
  const [removing, setRemoving] = useState(false);

  // Success snackbar
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const client = useMemo(() => new ApiClient(idToken), [idToken]);

  const fetchUsers = useCallback(async () => {
    if (!idToken) return;
    try {
      setLoading(true);
      setError(null);
      const data = await client.getPartnerUsersList(
        {
          search: search || undefined,
          page: page + 1,
          limit: rowsPerPage,
        },
        activePartnerId
      );
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      console.error('[ClientUserManagement] Failed to fetch users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [idToken, search, page, rowsPerPage, client, activePartnerId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditUid(null);
    setFormFirstName('');
    setFormLastName('');
    setFormEmail('');
    setFormRoles([]);
    setFormActive(true);
    setSaveError(null);
    setSubmitted(false);
  };

  // Open edit modal
  const openEditModal = (user: PartnerUserItem) => {
    setModalMode('edit');
    setEditUid(user.uid);
    setFormFirstName(user.firstName || '');
    setFormLastName(user.lastName || '');
    setFormEmail(user.email || '');
    setFormRoles(user.roles.filter((r) => !HIDDEN_ROLES.has(r)));
    setFormActive(!user.disabled);
    setSaveError(null);
    setSubmitted(false);
  };

  const closeModal = () => {
    setModalMode(null);
    setEditUid(null);
    setSaveError(null);
  };

  const toggleRole = (role: string) => {
    setFormRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const firstNameError = submitted && !formFirstName.trim() ? 'First name is required.' : '';
  const lastNameError = submitted && !formLastName.trim() ? 'Last name is required.' : '';
  const emailError =
    submitted && !formEmail.trim()
      ? 'Email is required.'
      : submitted && formEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim())
        ? 'Invalid email format.'
        : '';
  // Save (create or edit)
  const handleSave = async () => {
    setSubmitted(true);

    if (!formFirstName.trim() || !formLastName.trim() || !formEmail.trim()) return;
    if (formEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim())) return;

    try {
      setSaving(true);
      setSaveError(null);

      if (modalMode === 'create') {
        const result = await client.createPartnerUser({
          firstName: formFirstName.trim(),
          lastName: formLastName.trim(),
          email: formEmail.trim(),
          roles: formRoles,
          active: formActive,
        });
        setSnackbar(result.message);
      } else if (modalMode === 'edit' && editUid) {
        // Update profile, roles, and disabled status in a single API call
        const currentUser = users.find((u) => u.uid === editUid);
        // formActive=true means user should be enabled (disabled=false), so compare inverted
        const newDisabled = !formActive;
        const disabledChanged = currentUser && currentUser.disabled !== newDisabled;
        const result = await client.updatePartnerUser(editUid, {
          firstName: formFirstName.trim(),
          lastName: formLastName.trim(),
          email: formEmail.trim(),
          roles: formRoles,
          ...(disabledChanged ? { disabled: newDisabled } : {}),
        });
        setSnackbar(result.message);
      }

      closeModal();
      fetchUsers();
    } catch (err) {
      console.error('[ClientUserManagement] Save failed:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // Toggle disable/enable directly from table
  const handleToggleDisabled = async (user: PartnerUserItem) => {
    try {
      const result = await client.setPartnerUserDisabled(user.uid, !user.disabled);
      setSnackbar(result.message);
      setUsers((prev) =>
        prev.map((u) => (u.uid === user.uid ? { ...u, disabled: !user.disabled } : u))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    }
  };

  // Remove user
  const handleRemove = async () => {
    if (!removeUser) return;
    try {
      setRemoving(true);
      const result = await client.removePartnerUser(removeUser.uid);
      setSnackbar(result.message);
      setRemoveUser(null);
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 4, pb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h1" gutterBottom>
                Manage Users
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create, edit, and manage users within your organization
              </Typography>
            </Box>
            {!isInternal && (
              <Button
                variant="contained"
                onClick={openCreateModal}
                sx={{ mt: 0.5 }}
                startIcon={<i className="fa-thin fa-user-plus" style={{ fontSize: 16 }} />}
              >
                Create User
              </Button>
            )}
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i
                      className="fa-thin fa-magnifying-glass"
                      style={{ color: 'rgba(0,0,0,0.54)', fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <i className="fa-thin fa-xmark" style={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
              sx={{ height: 40 }}
            >
              Search
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <ImpersonationReadOnlyBanner />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No users found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => {
                      const isSelf = u.uid === currentUser?.uid;
                      return (
                        <TableRow key={u.uid} hover sx={{ opacity: u.disabled ? 0.5 : 1 }}>
                          <TableCell>{u.firstName || '—'}</TableCell>
                          <TableCell>{u.lastName || '—'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {u.email ?? '—'}
                              {isSelf && (
                                <Chip
                                  label="You"
                                  size="small"
                                  sx={{ ml: 1, fontSize: '0.65rem', height: 18 }}
                                />
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={u.disabled ? 'Disabled' : 'Active'}
                              size="small"
                              onClick={
                                isSelf || isInternal ? undefined : () => handleToggleDisabled(u)
                              }
                              sx={{
                                bgcolor: u.disabled ? '#F5F5F5' : '#E8F5E9',
                                color: u.disabled ? '#616161' : '#2E7D32',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                cursor: isSelf || isInternal ? 'default' : 'pointer',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <RoleChips roles={u.roles} />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Button
                                size="small"
                                onClick={() => openEditModal(u)}
                                disabled={isSelf || isInternal}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setRemoveUser(u)}
                                disabled={isSelf || isInternal}
                              >
                                Remove
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Container>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={!!modalMode} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{modalMode === 'create' ? 'Create User' : 'Edit User'}</DialogTitle>
        <DialogContent dividers>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                value={formFirstName}
                onChange={(e) => setFormFirstName(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!firstNameError}
                helperText={firstNameError}
              />
              <TextField
                label="Last Name"
                value={formLastName}
                onChange={(e) => setFormLastName(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!lastNameError}
                helperText={lastNameError}
              />
            </Stack>

            <TextField
              label="Email"
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              fullWidth
              size="small"
              required
              error={!!emailError}
              helperText={emailError}
            />

            {modalMode === 'create' && (
              <Alert severity="info">
                The user will receive an email invitation with a secure link to set their own
                password. The link expires after 48 hours.
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch checked={formActive} onChange={(e) => setFormActive(e.target.checked)} />
              }
              label={formActive ? 'Active' : 'Disabled'}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Roles
              </Typography>
              <FormGroup>
                {CLIENT_ROLES.map((role) => (
                  <FormControlLabel
                    key={role.key}
                    control={
                      <Checkbox
                        checked={formRoles.includes(role.key)}
                        onChange={() => toggleRole(role.key)}
                        size="small"
                      />
                    }
                    label={role.label}
                  />
                ))}
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : modalMode === 'create' ? 'Create User' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!removeUser} onClose={() => setRemoveUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{' '}
            <strong>
              {removeUser?.firstName} {removeUser?.lastName}
            </strong>
            ? This will revoke their access to the Partner Portal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveUser(null)} disabled={removing}>
            Cancel
          </Button>
          <Button onClick={handleRemove} color="error" variant="contained" disabled={removing}>
            {removing ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </>
  );
}
